import React, { useCallback, useState, useRef, useEffect } from 'react';

import { ContextProviderProps, CredentialContext, useWebSocketContext } from '.';
import { useTractionService } from '../services';
import { Credential, CredentialExchangeRecord, CredentialProofRequest } from '../types/credentials';
import { CredentialState } from '../types/enums';
import log from '../logger';

/**
 * @description Context provider for credentials
 * @param {ContextProviderProps} { children }
 * @return {React.FC}
 */
const ContextProvider: React.FC<ContextProviderProps> = ({ children }) => {
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [offers, setOffers] = useState<CredentialExchangeRecord[]>([]);
  const [proofs, setProofs] = useState<CredentialProofRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const { lastMessage } = useWebSocketContext();
  const service = useTractionService();
  const fetching = useRef(false);

  const fetchAllItems = useCallback(async () => {
    if (!fetching.current) {
      try {
        fetching.current = true;
        setLoading(true);
        const data = await service.getAllItems();

        if (!data || !data.credentials || !data.offers || !data.proofs) {
          return;
        }

        const credByReferent = data.credentials.reduce<
          Record<string, (typeof data.credentials)[0]>
        >((map, cred) => {
          map[cred.referentId] = cred;
          return map;
        }, {});

        const proofs = data.proofs.map((proof) => {
          const credentialsWithRevocation = proof.credentials.map((cred) => {
            const matchingCred = credByReferent[cred.referentId];
            return {
              ...cred,
              revoked: matchingCred?.revoked
            };
          });

          return {
            ...proof,
            credentials: credentialsWithRevocation
          };
        });
        setCredentials(data.credentials);
        setOffers(data.offers);
        setProofs(proofs);
      } catch (error) {
        log.error('Error fetching all items: ', error);
      } finally {
        setLoading(false);
        fetching.current = false;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCredentials = useCallback(async () => {
    if (!fetching.current) {
      try {
        fetching.current = true;
        const data = await service.getCredentialRecords();
        setCredentials(data);
      } catch (error) {
        log.error('Error fetching credentials: ', error);
      } finally {
        setLoading(false);
        fetching.current = false;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchOffers = useCallback(async () => {
    if (!fetching.current) {
      fetching.current = true;
      try {
        const data = await service.getCredentialRecords({
          state: CredentialState.OfferReceived
        });
        setOffers(data);
      } catch (error) {
        log.error('Error fetching offers: ', error);
      } finally {
        setLoading(false);
        fetching.current = false;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProofs = useCallback(async () => {
    if (!fetching.current) {
      fetching.current = true;
      try {
        const proofsData = await service.getProofRecords({
          state: CredentialState.RequestReceived
        });
        const credByReferent = credentials.reduce<Record<string, (typeof credentials)[0]>>(
          (map, cred) => {
            map[cred.referentId] = cred;
            return map;
          },
          {}
        );
        const revokedProofs = proofsData.map((proof) => {
          const credentialsWithRevocation = proof.credentials.map((cred) => {
            const matchingCred = credByReferent[cred.referentId];
            return {
              ...cred,
              revoked: matchingCred?.revoked || false
            };
          });

          return {
            ...proof,
            credentials: credentialsWithRevocation
          };
        });
        setProofs(revokedProofs);
      } catch (error) {
        log.error('Error fetching proofs: ', error);
      } finally {
        setLoading(false);
        fetching.current = false;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service, credentials]);

  const addCredential = useCallback(
    (credential: Credential) => {
      setCredentials((prev) => [credential, ...prev]);
      fetchCredentials();
    },
    [fetchCredentials]
  );

  const removeOffer = useCallback(
    (credential: CredentialExchangeRecord) => {
      const newOffers = offers.filter(
        (offer) => offer.credentialExchangeId !== credential.credentialExchangeId
      );
      setOffers(newOffers);
      return newOffers.length;
    },
    [offers]
  );

  const removeCredential = useCallback(
    (credential: Credential) => {
      const newCredentials = credentials.filter(
        (cred) => cred.credentialExchangeId !== credential.credentialExchangeId
      );
      setCredentials(newCredentials);
      return newCredentials.length;
    },
    [credentials]
  );

  const removeProof = useCallback(
    (request: CredentialProofRequest) => {
      const newProofs = proofs.filter(
        (proof) => proof.presentationExchangeId !== request.presentationExchangeId
      );
      setProofs(newProofs);
      return newProofs.length;
    },
    [proofs]
  );

  useEffect(() => {
    try {
      if (!lastMessage) return;

      // Check if the message data is valid JSON before parsing
      let message;
      try {
        message = JSON.parse(lastMessage?.data);
      } catch {
        // Skip non-JSON messages (like ping/pong, raw text, etc.)
        return;
      }

      if (message.message === 'credential_revoked') {
        const revokedThreadId = message.cred_ex_id;
        setCredentials((prevCreds) => {
          const updatedCreds = prevCreds.map((cred) =>
            cred.threadId === revokedThreadId ? { ...cred, revoked: true } : cred
          );
          return updatedCreds;
        });
        const revokedCred = credentials.find((c) => c.threadId === revokedThreadId);
        const referentId = revokedCred?.referentId;
        if (!referentId) return;
        setProofs((prevProofs) =>
          prevProofs.map((proof) =>
            proof.referentId === referentId ? { ...proof, revoked: true } : proof
          )
        );
      }
    } catch (e) {
      log.error('Error handling message: ', e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessage]);
  return (
    <CredentialContext.Provider
      value={{
        credentials,
        fetchCredentials,
        offers,
        fetchOffers,
        proofs,
        fetchProofs,
        loading,
        fetchAllItems,
        addCredential,
        removeOffer,
        removeProof,
        removeCredential
      }}>
      {children}
    </CredentialContext.Provider>
  );
};

export default ContextProvider;
